/* eslint-disable no-restricted-syntax */
import React, { useContext, useEffect, useState, useRef } from 'react';
import { List, DataColumn, LinkColumn, ListHeader } from 'components/lists';
import { useAggregate } from 'hooks';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Config } from 'configs';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageTitle } from 'components/layouts/pageTitle';
import { DropDownList } from 'components/formInputs/dropDownList';
import { Pagination } from 'components/lists/pagination';
import { PageContent } from 'components/layouts/pageContent';
import { FilterBlock, FilterField } from 'components/layouts/filterBlock';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { getAvailableTranslatableText } from 'utils/schemaHelper';
import { ListLayout } from 'components/layouts/listLayout';
import { NoRestaurantsBlock } from 'components/blocks/noRestaurants';
import { Input } from 'components/formInputs/input';
import { formFileUrlFromFileArrayWithExternalResized } from 'utils/imageHelper';
import { extractRecordsFromAggreagte } from 'utils/renderingHelpers';
import { getTranslatedData } from 'locales';
import { CheckCircleIcon } from '@heroicons/react/solid';

export const Products = () => {
  const { state: auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const { state: companyState, getCompanyRestaurants, deleteById, shouldDisplayNoRestaurantBlock } = useContext(CompanyContext);
  const [currentPage, setCurrentPage] = useState(1);

  const [filterRestaurant, setFilterRestaurant, filterRestaurantRef] = useLocalStorage(`products-filter-restaurant-${auth.company?.company}`, null);
  const [filterCategory, setFilterCategory, filterCategoryRef] = useLocalStorage(`products-filter-category-${auth.company?.company}`, null);
  const [filterHidden, setFilterHidden, filterHiddenRef] = useLocalStorage(`products-filter-hidden-${auth.company?.company}`, 'visible');
  const [filterText, setFilterText, filterTextRef] = useLocalStorage(`products-filter-search-${auth.company?.company}`, '');
  const initRestaurantFilter = useRef({ value: null }).current;

  const [sort, setSort] = useLocalStorage(`products-sort-${auth.company?.company}`, { field: 'name', dbField: 'name', order: 1 });
  const changedSort = useRef({ value: null }).current;

  const location = useLocation();
  const { search } = location;

  const requestData = {
    collectionName: Config.DATA.COLLECTIONS.PRODUCTS,
    aggregateId: Config.DATA.AGGREGATES.GET_PRODUCTS_LIST,
    secretKey: auth.token,
    filterBuilder: () => {
      const filter = { companyId: auth.company?.company };

      if (initRestaurantFilter.value || filterRestaurantRef.value) {
        filter.restaurantFilter = `{ restaurants: { $eq: ObjectId('${initRestaurantFilter.value || filterRestaurantRef.value}') } }`;
        initRestaurantFilter.value = null;
      } else {
        filter.restaurantFilter = {};
      }

      if (filterCategoryRef.value) {
        filter.categoriesFilter = `{ categories: { $eq: ObjectId('${filterCategoryRef.value}') } }`;
      } else {
        filter.categoriesFilter = {};
      }

      if (filterHiddenRef.value) {
        if (filterHiddenRef.value === 'hidden') {
          filter.hiddenFilter = '{ hidden: { $eq: true } }';
        } else if (filterHiddenRef.value === 'visible') {
          filter.hiddenFilter = '{ hidden: { $ne: true } }';
        } else {
          filter.hiddenFilter = {};
        }
      } else {
        filter.hiddenFilter = {};
      }

      if (filterTextRef.value?.trim()) {
        const textSearch = filterTextRef.value?.trim();

        filter.searchFilter = `{ $or: [{ 'name.en': { $regex: '.*${textSearch}.*', $options: 'i' } },
        { 'description.en': { $regex: '.*${textSearch}.*', $options: 'i' } }]}`;
      } else {
        filter.searchFilter = {};
      }

      return filter;
    },
    sortBuilder: () => {
      const sortKey = changedSort.value?.dbField || sort?.dbField || 'name';

      if (Array.isArray(sortKey)) {
        const tempSort = {};

        sortKey.forEach((key) => {
          tempSort[`'${key}'`] = changedSort.value?.order || sort?.order || -1;
        });
        return tempSort;
      }

      const tempSort = { [`${sortKey}`]: changedSort.value?.order || sort?.order || -1 };

      return tempSort;
    },
    pageSize: Config.DEFAULTS.PAGE_SIZE,
    pageNumber: currentPage - 1,
    loadOnInit: true,
    dontRunOnInit: true,
    aggregateIdV2: 'AdminListProducts',
    companyIdForV2: auth.company?.company,
  };

  const [findProducts, productsResponse, isLoading,, setIsProductsLoading] = useAggregate(requestData);

  const { paginatedResults: products, totalCount } = extractRecordsFromAggreagte(productsResponse);

  const [, productCategories, isLoadingProducCategories] = useAggregate({
    collectionName: Config.DATA.COLLECTIONS.PRODUCT_CATEGORIES,
    aggregateId: Config.DATA.AGGREGATES.PRODUCT_CATEGORIES_V2,
    secretKey: auth.token,
    filter: { company: auth.company?.company },
    pageSize: 100,
    pageNumber: 0,
    loadOnInit: true,
    aggregateIdV2: 'AdminGetProductCategories',
    companyIdForV2: auth.company?.company,
  });

  useEffect(() => {
    const fetchRestaurants = async () => {
      const restaurants = await getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });

      if (restaurants && restaurants.length > 0) {
        const sp = new URLSearchParams(search);
        const restaurantFromUrl = sp.get('restaurant');

        const firstRestaurantId = restaurantFromUrl || restaurants[0]._id;

        if (!filterRestaurant) {
          initRestaurantFilter.value = firstRestaurantId;
          setFilterRestaurant(firstRestaurantId);
        }

        await findProducts();
      } else {
        setIsProductsLoading(Config.DATA.LOADING_STATUS.NOT_LOADING);
      }
    };

    fetchRestaurants();
  }, []);

  const onFilterProducts = () => {
    findProducts();
  };

  const onClearFilters = () => {
    setFilterCategory('');
    setFilterText('');
    setFilterHidden('visible');
    onFilterProducts();
  };

  const onSortProducts = (field, dbField, order) => {
    setSort({ field, dbField, order });
    changedSort.value = { field, dbField, order };
    onFilterProducts();
  };

  const renderProductCategory = (cats, productAssignedCategories) => {
    if (!cats) {
      return null;
    }

    if (!productAssignedCategories || productAssignedCategories.length === 0) {
      return null;
    }

    const foundNames = [];

    productAssignedCategories.forEach((assignedCategoryId) => {
      const foundData = cats?.find((x) => x._id === assignedCategoryId);

      if (foundData) {
        foundNames.push(getAvailableTranslatableText(foundData.name));
      }
    });

    return foundNames.join(', ');
  };

  const onEditClicked = (item) => {
    navigate(`/products/${item._id}`);
  };

  const onDeleteClicked = async (item) => {
    await deleteById(
      {
        collectionName: Config.DATA.COLLECTIONS.PRODUCTS,
        token: auth.token,
        id: item._id,
        resourceV2: 'AdminProduct',
        companyIdForV2: auth.company?.company,
      },
      async () => {
        await findProducts(true, requestData);
      },
    );
  };

  const finalIsLoading = isLoading === Config.DATA.LOADING_STATUS.INITIAL_LOAD
    || isLoadingProducCategories;

  if (isLoading !== Config.DATA.LOADING_STATUS.INITIAL_LOAD && shouldDisplayNoRestaurantBlock()) {
    return <NoRestaurantsBlock />;
  }

  return (
    <ListLayout isLoading={finalIsLoading || companyState.isLoadingRestaurants}>
      <PageTitle title="Products" isTopLevel>

        <button
          type="button"
          onClick={() => navigate('/products/imports')}
          className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
        >
          Import / Export
        </button>
      </PageTitle>
      <PageContent>
        <FilterBlock buttons={[
          {
            onPress: onClearFilters,
            text: 'Clear',
            class: 'bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 inline-flex justify-center items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
            disabled: !!isLoading,
          },
          {
            onPress: () => onFilterProducts(),
            text: 'Search',
            class: 'ml-3 bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center items-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
            disabled: !!isLoading,
            isLoading: !!isLoading,
          },
        ]}
        >
          <FilterField>
            <DropDownList
              showLabel
              id="restaurants"
              name="restaurants"
              label="Restaurant"
              defaultValue={filterRestaurant}
              onChange={(e) => {
                setFilterRestaurant(e.target.value);
                setFilterCategory('');
              }}
              options={companyState.restaurants?.map((restaurant) => ({
                label: restaurant.name,
                value: restaurant._id,
              }))}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            />
          </FilterField>
          <FilterField>
            <DropDownList
              showLabel
              id="categories"
              name="categories"
              label="Category"
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
              }}
              options={[{ label: 'All', value: '' }, ...(productCategories?.filter((categ) => {
                if (filterRestaurant && categ.restaurants && categ.restaurants.includes(filterRestaurant)) {
                  return true;
                }

                return false;
              })?.map((category) => ({
                label: getAvailableTranslatableText(category.name),
                value: category._id,
              })) || [])]}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            />
          </FilterField>
          <FilterField>
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                Search
              </label>
              <div className="mt-1 relative text-sm text-gray-500">
                <Input
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  name="search"
                  id="search"
                  type="text"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  onEnterClicked={() => {
                    onFilterProducts();
                  }}
                  onFocus={(event) => event.target.select()}
                />
              </div>
            </div>
          </FilterField>
          <FilterField>
            <DropDownList
              showLabel
              id="hidden"
              name="hidden"
              label="Hidden"
              value={filterHidden}
              onChange={(e) => {
                setFilterHidden(e.target.value);
              }}
              options={Config.DATA.FILTER_HIDDEN_OPTIONS}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            />
          </FilterField>
        </FilterBlock>
        <ListHeader title="Products" description={`Products of ${auth.company?.display_name}`}>
          <button
            type="button"
            onClick={() => navigate('/products/add-new')}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add new
          </button>
        </ListHeader>
        <List source={products} sort={sort} onRowClick={onEditClicked} editCollection="AdminProductList">
          <DataColumn
            name="Image"
            render={(product) => {
              const imageUrl = formFileUrlFromFileArrayWithExternalResized(product.list_image, product.list_image_resized);

              if (!imageUrl) {
                return null;
              }

              return (
                <div
                  className="w-10 h-10 rounded-lg bg-no-repeat bg-center bg-cover"
                  style={{ backgroundImage: `url(${imageUrl})` }}
                />
              );
            }}
            dbColumnName="list_image"
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-700 sm:table-cell"
          />
          <DataColumn
            name="Name"
            dbColumnName="name"
            render={(product) => {
              const description = getTranslatedData(product.description);

              return (
                <div>
                  <p>{getTranslatedData(product.name)}</p>
                  {!!description && <p className="text-gray-400 mt-1">{description}</p>}
                </div>
              );
            }}
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="px-3 py-4 text-sm text-gray-500 sm:table-cell min-w-[200px] max-w-[300px]"
            /* stackedColumns={[
              { name: 'Categories', render: (product) => renderProductCategory(productCategories, product.categories), class: 'mt-1 truncate text-gray-400 sm:hidden' },
            ]} */
            onSort={(sortOrder) => {
              onSortProducts('name', 'name', sortOrder);
            }}
          />
          <DataColumn
            name="Price"
            dbColumnName="prime_cost"
            render={(product) => {
              return `${(product.prime_cost || 0).toFixed(2)} Eur`;
            }}
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
            onSort={(sortOrder) => {
              onSortProducts('prime_cost', 'prime_cost', sortOrder);
            }}
            editSettings={{
              type: 'float',
              valueField: 'prime_cost',
              dbField: 'prime_cost',
            }}
          />
          <DataColumn
            name="Packaging (takeout)"
            dbColumnName="package_cost"
            render={(product) => {
              return `${(product.package_cost || 0).toFixed(2)} Eur`;
            }}
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
            onSort={(sortOrder) => {
              onSortProducts('package_cost', 'package_cost', sortOrder);
            }}
            editSettings={{
              type: 'float',
              valueField: 'package_cost',
              dbField: 'package_cost',
            }}
          />
          <DataColumn
            name="Packaging (delivery)"
            dbColumnName="package_cost_delivery"
            render={(product) => {
              return `${(product.package_cost_delivery || product.package_cost || 0).toFixed(2)} Eur`;
            }}
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
            onSort={(sortOrder) => {
              onSortProducts('package_cost_delivery', 'package_cost_delivery', sortOrder);
            }}
            editSettings={{
              type: 'float',
              valueField: 'package_cost_delivery',
              dbField: 'package_cost_delivery',
            }}
          />
          <DataColumn
            name="Categories"
            render={(product) => {
              const hasOverrides = product.categories_override && product.categories_override.length > 0;

              return (
                <div>
                  <div className={`${hasOverrides ? 'line-through' : ''}`}>{renderProductCategory(productCategories, product.categories)}</div>
                  {hasOverrides && <div>{renderProductCategory(productCategories, product.categories_override)}</div>}
                </div>
              );
            }}
            dbColumnName="categories"
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="px-3 py-4 text-sm text-gray-500 sm:table-cell"
            onSort={(sortOrder) => {
              onSortProducts('categories', ['categories', 'order'], sortOrder);
            }}
          />
          <DataColumn
            name="Schedule"
            dbColumnName="schedule"
            render={(product) => {
              return product.schedule && product.schedule.length > 0 ?
                <CheckCircleIcon className="h-5 w-5" aria-hidden="true" /> : null;
            }}
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Time"
            dbColumnName="delivery_time"
            render={(product) => {
              if (product.prepare_from && product.prepare_to) {
                return <div>{product.prepare_from} min -<div>{product.prepare_to} min</div></div>;
              }

              if (product.prepare_from || product.prepare_to) {
                return `${product.prepare_from || product.prepare_to || ''} min`;
              }

              return null;
            }}
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Options"
            dbColumnName="options"
            render={(product) => {
              return (
                <div>
                  {product.is_new && <div>New</div>}
                  {product.is_popular && <div>Popular</div>}
                  {product.is_vegetarian && <div>Vegetarian</div>}
                  {product.spicy > 0 && <div>Spicy ({product.spicy})</div>}
                  {product.is_recommended && <div>Recommended</div>}
                  {product.is_age_restricted && <div>Age restricted</div>}
                </div>
              );
            }}
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Order"
            dbColumnName="order"
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
            onSort={(sortOrder) => {
              onSortProducts('order', 'order', sortOrder);
            }}
            editSettings={{
              type: 'integer',
              valueField: 'order',
              dbField: 'order',
            }}
          />
          <DataColumn
            name="Hidden"
            dbColumnName="hidden"
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
            editSettings={{
              type: 'boolean',
              valueField: 'hidden',
              dbField: 'hidden',
            }}
          />
          <LinkColumn
            title="Delete"
            onClick={onDeleteClicked}
            askForConfirmation
            className="relative py-3.5 pl-3 sm:pr-6"
            dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
          />
          <Pagination
            pageSize={Config.DEFAULTS.PAGE_SIZE}
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChanged={async (pageNumber) => {
              await findProducts(false, { ...requestData, pageNumber: pageNumber - 1 });
              setCurrentPage(pageNumber);
            }}
          />
        </List>
      </PageContent>
    </ListLayout>
  );
};
